// sdfBuilder.js
// Generador robusto de grilla SDF (Signed Distance Field) a partir de triángulos 3D.
// Compatible con sistema modular del reparador STL (3dfyrionreparadorstl.js).

import { pointTriangleDistance, rayIntersectsTriangle } from "./geometryUtils.js";

/**
 * buildSDFGrid
 * Construye un campo de distancia firmado (SDF) para una malla triangular.
 * @param {Array} tris - Lista de triángulos {A:[x,y,z], B:[x,y,z], C:[x,y,z]}.
 * @param {Object} bbox - {min:[x,y,z], max:[x,y,z]} bounding box del modelo.
 * @param {number} nx,ny,nz - resolución de la grilla 3D.
 * @param {function} setProgress - callback opcional de progreso.
 * @param {Object} options - opciones adicionales (p.ej. cancelFlag, optimizeRadius).
 * @returns {Object} {grid, dx, dy, dz, nx, ny, nz, min:[x,y,z]}
 */
export function buildSDFGrid(tris, bbox, nx, ny, nz, setProgress, options = {}) {
  const { cancelFlag, optimizeRadius = true } = options;
  const [minX, minY, minZ] = bbox.min;
  const [maxX, maxY, maxZ] = bbox.max;

  const sizeX = maxX - minX;
  const sizeY = maxY - minY;
  const sizeZ = maxZ - minZ;

  const dx = sizeX / (nx - 1);
  const dy = sizeY / (ny - 1);
  const dz = sizeZ / (nz - 1);

  const grid = new Float32Array(nx * ny * nz);
  const dir = [1, 0, 0]; // dirección de rayo para prueba inside/outside (+X)

  // Precalcular bounding boxes por triángulo
  const triBoxes = tris.map(t => ({
    min: [
      Math.min(t.A[0], t.B[0], t.C[0]),
      Math.min(t.A[1], t.B[1], t.C[1]),
      Math.min(t.A[2], t.B[2], t.C[2])
    ],
    max: [
      Math.max(t.A[0], t.B[0], t.C[0]),
      Math.max(t.A[1], t.B[1], t.C[1]),
      Math.max(t.A[2], t.B[2], t.C[2])
    ]
  }));

  // Función auxiliar: test rápido AABB vs esfera (para saltar triángulos lejanos)
  function bboxIntersectsSphere(bmin, bmax, p, r) {
    let d2 = 0;
    for (let i = 0; i < 3; i++) {
      const v = p[i];
      if (v < bmin[i]) d2 += (bmin[i] - v) ** 2;
      else if (v > bmax[i]) d2 += (v - bmax[i]) ** 2;
    }
    return d2 <= r * r;
  }

  const total = nx * ny * nz;
  const reportEvery = Math.max(1, Math.floor(nz / 8));
  let idx = 0;

  for (let iz = 0; iz < nz; iz++) {
    if (cancelFlag?.cancelled) break; // cancelación segura

    for (let iy = 0; iy < ny; iy++) {
      for (let ix = 0; ix < nx; ix++, idx++) {
        const p = [minX + ix * dx, minY + iy * dy, minZ + iz * dz];
        let bestDist = Infinity;

        // Paso 1: distancia más cercana a triángulo
        for (let t = 0; t < tris.length; t++) {
          const box = triBoxes[t];
          if (optimizeRadius && !bboxIntersectsSphere(box.min, box.max, p, bestDist))
            continue; // ignorar triángulos lejanos
          const d = pointTriangleDistance(p, tris[t].A, tris[t].B, tris[t].C);
          if (d < bestDist) bestDist = d;
        }

        // Paso 2: determinar si punto está dentro (raycast +X)
        let intersections = 0;
        const rayOrig = [minX - sizeX * 2, p[1], p[2]]; // origen fuera del modelo
        for (let t = 0; t < tris.length; t++) {
          if (rayIntersectsTriangle(rayOrig, dir, tris[t].A, tris[t].B, tris[t].C))
            intersections++;
        }

        // Convención: negativo = dentro, positivo = fuera
        grid[idx] = (intersections % 2 === 1) ? -bestDist : bestDist;
      }
    }

    if (setProgress && iz % reportEvery === 0)
      setProgress(`Voxelizando capa ${iz + 1}/${nz} (${Math.round((iz / nz) * 100)}%)`);
  }

  if (setProgress) setProgress("Voxelización completada ✅");
  return { grid, dx, dy, dz, nx, ny, nz, min: [minX, minY, minZ] };
}
